import { useMemo } from "react";
import { Grid, Button, Dropdown } from "semantic-ui-react";
import { Formik, Form } from "formik";
import * as yup from "yup";
import { ErrorInfo, FieldRow } from "components/form";
import { toast } from "components/Toast/Toast";
import { validateEmail, validatePhoneNumber, usersToSelectOptions } from "services/common";
import { AgencyType } from "models";

export const PrintServiceForm = ({
  printServiceToUpdate,
  closePrintServiceForm,
  users,
  dispatch,
  t,
}) => {
  const printServiceSchema = yup.object().shape({
    name: yup.string().required(t("required")),
    phone: yup
      .string()
      .required(t("required"))
      .test("is-phone-nr", t("invalid_phone_number"), validatePhoneNumber),
    email: yup.string().required(t("required")).test("is-email", t("invalid_email"), validateEmail),
    address: yup.string().required(t("required")),
    postalNumber: yup.string().required(t("required")),
    city: yup.string().required(t("required")),
    country: yup.string().required(t("required")),
    contacts: yup.array().min(1, t("min_one_contact_error")),
  });

  const initialValues = useMemo(
    () =>
      !printServiceToUpdate
        ? {
            name: "",
            phone: "",
            email: "",
            address: "",
            postalNumber: "",
            city: "",
            country: "",
            contacts: [],
          }
        : {
            name: printServiceToUpdate.name,
            phone: printServiceToUpdate.phone,
            email: printServiceToUpdate.email,
            address: printServiceToUpdate.address,
            postalNumber: printServiceToUpdate.postalNumber,
            city: printServiceToUpdate.city,
            country: printServiceToUpdate.country,
            contacts: printServiceToUpdate.contacts,
          },
    [printServiceToUpdate]
  );

  const submitCreate = async (values) => {
    try {
      await dispatch.agencys.create({ ...values, type: AgencyType.PRINT_SERVICE });
      toast(undefined, t("print_service_create_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("print_service_create_failure"));
    }
    closePrintServiceForm();
  };

  const submitUpdate = async (values) => {
    try {
      await dispatch.agencys.update({ id: printServiceToUpdate.id, ...values });
      toast(undefined, t("print_service_update_success"));
    } catch (error) {
      console.error(error);
      toast("error", t("print_service_update_failure"));
    }
    closePrintServiceForm();
  };

  return (
    <Formik
      validationSchema={printServiceSchema}
      initialValues={initialValues}
      onSubmit={printServiceToUpdate ? submitUpdate : submitCreate}
    >
      {({ values, errors, isSubmitting, setFieldValue, submitCount }) => (
        <Form>
          <h3>{printServiceToUpdate ? t("print_service_update") : t("print_service_create")}:</h3>
          <Grid columns={2} verticalAlign="middle">
            <FieldRow label={t("name")} name="name" />
            <FieldRow label={t("phone")} name="phone" />
            <FieldRow label={t("email")} name="email" />
            <FieldRow label={t("address")} name="address" />
            <FieldRow label={t("postalNumber")} name="postalNumber" />
            <FieldRow label={t("city")} name="city" />
            <FieldRow label={t("country")} name="country" />
            <Grid.Row className="form-field-row">
              <Grid.Column width={3} textAlign="right">
                <label htmlFor="contacts">{t("contacts")}</label>
              </Grid.Column>
              <Grid.Column width={13}>
                <Dropdown
                  name="contacts"
                  placeholder={t("contacts_placeholder")}
                  value={values.contacts}
                  fluid
                  multiple
                  search
                  selection
                  options={usersToSelectOptions(users)}
                  onChange={(_e, { value, name }) => setFieldValue(name, value)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
          <ErrorInfo errors={errors} submitCount={submitCount} t={t} tPrefix={""} />
          <div className="form-footer">
            <Button type="button" primary onClick={() => closePrintServiceForm()}>
              {t("cancel")}
            </Button>
            <Button primary type="submit" loading={isSubmitting}>
              {printServiceToUpdate ? t("update") : t("create")}
            </Button>
          </div>
        </Form>
      )}
    </Formik>
  );
};
